import { useState } from "react";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { formatBreakEven, formatMoney, formatPercent } from "@shared/roi/format";
import type { ProductDetail } from "@shared/schemas/product";
import type { ScenarioRow, VisibilityName } from "@shared/schemas/scenario";
import { Badge } from "@client/components/Badge";
import { Button } from "@client/components/Button";
import { ConfirmDialog } from "@client/components/ConfirmDialog";
import { EmptyState } from "@client/components/EmptyState";
import { SelectField, TextField } from "@client/components/Field";
import { ImportScenarioDialog } from "@client/components/ImportScenarioDialog";
import { Modal } from "@client/components/Modal";
import { NewScenarioDialog } from "@client/components/NewScenarioDialog";
import { ErrorNotice, LoadingBlock } from "@client/components/Notices";
import { Panel } from "@client/components/Panel";
import { ProductForm } from "@client/components/ProductForm";
import { ProductMembers } from "@client/components/ProductMembers";
import { ScenarioTable } from "@client/components/ScenarioTable";
import { ShareDialog } from "@client/components/ShareDialog";
import { StatTile } from "@client/components/StatTile";
import { MAX_COMPARE_SCENARIOS } from "@shared/constants";
import { useDebouncedValue } from "@client/hooks/useDebouncedValue";
import { useArchiveProduct, useProduct, useUpdateProduct } from "@client/hooks/useProducts";
import {
  useArchiveScenario,
  useDuplicateScenario,
  usePatchScenario,
  useScenarios,
} from "@client/hooks/useScenarios";
import { compareSearch } from "@client/lib/compareIds";
import { describeError } from "@client/lib/errorMessage";

const route = getRouteApi("/_authed/products/$slug");

/**
 * One product: what it is, the baseline everyone forks, and every scenario on it
 * this person is allowed to see.
 *
 * The list is where a scenario is created, forked, duplicated, shared, archived
 * and crowned — and, since phase 8, where two to four of them are picked out for
 * `/compare` and where a file becomes a scenario.
 */
export function ProductDetailPage() {
  const { slug } = route.useParams();
  const navigate = useNavigate();

  const product = useProduct(slug);

  const [search, setSearch] = useState("");
  const [visibility, setVisibility] = useState<"" | VisibilityName>("");
  const [mine, setMine] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const debouncedSearch = useDebouncedValue(search);

  const scenarios = useScenarios(product.data?.id, {
    q: debouncedSearch.trim() || undefined,
    visibility: visibility || undefined,
    mine: mine || undefined,
    includeArchived: includeArchived || undefined,
  });

  const duplicate = useDuplicateScenario();
  const archiveScenario = useArchiveScenario();
  const patchScenario = usePatchScenario();
  const archiveProduct = useArchiveProduct(product.data?.id ?? "");

  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  // The comparison selection is local to this page: it is a shortlist being
  // assembled, and only becomes an address — the thing worth sharing — when
  // "Compare" is pressed.
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [sharing, setSharing] = useState<ScenarioRow | null>(null);
  const [confirmArchiveProduct, setConfirmArchiveProduct] = useState(false);
  const [confirmArchiveScenario, setConfirmArchiveScenario] = useState<ScenarioRow | null>(null);

  if (product.isPending) return <LoadingBlock rows={4} label="Loading product" />;
  if (product.isError) {
    return <ErrorNotice error={product.error} onRetry={() => void product.refetch()} />;
  }

  const detail = product.data;
  const summary = detail.baselineSummary;
  const rows = scenarios.data?.items ?? [];
  const filtered = Boolean(debouncedSearch.trim() || visibility || mine || includeArchived);

  const toggleCompare = (id: string) =>
    setCompareIds((current) =>
      current.includes(id)
        ? current.filter((candidate) => candidate !== id)
        : current.length >= MAX_COMPARE_SCENARIOS
          ? current
          : [...current, id],
    );

  // Only the row a mutation is actually running for waits, so one slow archive
  // does not grey out every button in the table.
  const busyId =
    duplicate.isPending || archiveScenario.isPending || patchScenario.isPending
      ? (duplicate.variables?.id ?? archiveScenario.variables?.id ?? patchScenario.variables?.id)
      : undefined;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-ink">{detail.name}</h1>
            {detail.status !== "ACTIVE" && (
              <Badge kind={detail.status === "ARCHIVED" ? "warning" : "neutral"}>
                {detail.status === "ARCHIVED" ? "Archived" : "Draft"}
              </Badge>
            )}
          </div>
          {detail.description && (
            <p className="max-w-prose text-sm text-ink-soft">{detail.description}</p>
          )}
          <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-ink-soft">
            <span>{detail.owner.fullName}</span>
            <span aria-hidden="true">·</span>
            <span>{detail.currencyCode}</span>
            <span aria-hidden="true">·</span>
            <span className="tabular">{detail.defaultHorizonYears}-year horizon</span>
            <span aria-hidden="true">·</span>
            <span className="tabular">
              {detail.defaultCommitmentYears === 0
                ? "no commitment"
                : `${detail.defaultCommitmentYears}-year commitment`}
            </span>
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {detail.abilities.canCreateScenario && (
            <Button onClick={() => setCreating(true)}>New scenario</Button>
          )}
          {detail.abilities.canCreateScenario && (
            <Button variant="ghost" onClick={() => setImporting(true)}>
              Import
            </Button>
          )}
          {detail.abilities.canManage && (
            <Button variant="ghost" onClick={() => setEditing(true)}>
              Edit
            </Button>
          )}
          {detail.abilities.canArchive && (
            <Button
              variant={detail.archivedAt ? "ghost" : "destructive"}
              onClick={() => {
                if (detail.archivedAt) archiveProduct.mutate(true);
                else setConfirmArchiveProduct(true);
              }}
              disabled={archiveProduct.isPending}
            >
              {detail.archivedAt ? "Restore" : "Archive"}
            </Button>
          )}
        </div>
      </header>

      {archiveProduct.isError && <ErrorNotice error={archiveProduct.error} />}

      <Panel
        title="Baseline"
        description="The curated profile new scenarios fork from. Editing it is the product owner's call; everyone else takes a copy."
        actions={
          detail.abilities.canCreateScenario && (
            <Button size="sm" onClick={() => setCreating(true)}>
              Fork to my scenario
            </Button>
          )
        }
      >
        {summary && summary.roiPct !== null ? (
          <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
            <StatTile label="ROI" value={formatPercent(summary.roiPct)} />
            <StatTile
              label="Break-even"
              value={formatBreakEven(summary.breakEvenYears, detail.defaultHorizonYears)}
            />
            <StatTile
              label="Revenue"
              tone="revenue"
              value={formatMoney(detail.currencyCode, summary.revenue ?? 0, { compact: true })}
            />
            <StatTile
              label="Cost"
              tone="cost"
              value={formatMoney(detail.currencyCode, summary.cost ?? 0, { compact: true })}
            />
            <StatTile
              label="Net"
              tone={(summary.net ?? 0) >= 0 ? "revenue" : "cost"}
              value={formatMoney(detail.currencyCode, summary.net ?? 0, {
                compact: true,
                signed: true,
              })}
              hint={
                summary.endingActive === null
                  ? undefined
                  : `${summary.endingActive} active at the end`
              }
            />
          </div>
        ) : (
          <p className="text-sm text-ink-soft">
            {detail.baselineScenarioId
              ? "The baseline has no figures yet — nobody has saved a model into it. Forking it still works; you would start from its packages, such as they are."
              : "This product has no baseline. New scenarios start from an empty one until a product owner crowns a baseline."}
          </p>
        )}
      </Panel>

      <Panel
        title="Scenarios"
        description="Every scenario on this product you are allowed to see, with the figures from its last save."
      >
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[12rem] flex-1">
            <TextField
              label="Search"
              type="search"
              placeholder="Scenario name"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="w-44">
            <SelectField
              label="Visibility"
              value={visibility}
              onChange={(event) => setVisibility(event.target.value as "" | VisibilityName)}
            >
              <option value="">Any visibility</option>
              <option value="PRIVATE">Private</option>
              <option value="SHARED">Shared</option>
              <option value="PRODUCT">Product-wide</option>
            </SelectField>
          </div>
          <label className="flex items-center gap-2 py-2 text-xs font-semibold text-ink">
            <input
              type="checkbox"
              checked={mine}
              onChange={(event) => setMine(event.target.checked)}
              className="h-4 w-4 accent-[var(--azure-deep)]"
            />
            Only mine
          </label>
          <label className="flex items-center gap-2 py-2 text-xs font-semibold text-ink">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(event) => setIncludeArchived(event.target.checked)}
              className="h-4 w-4 accent-[var(--azure-deep)]"
            />
            Include archived
          </label>
        </div>

        {scenarios.isPending && <LoadingBlock rows={3} label="Loading scenarios" />}
        {scenarios.isError && (
          <ErrorNotice error={scenarios.error} onRetry={() => void scenarios.refetch()} />
        )}

        {scenarios.isSuccess && rows.length === 0 && (
          <EmptyState
            title={filtered ? "No scenarios match those filters" : "No scenarios yet"}
            description={
              filtered
                ? "Nothing here answers to that search. Archived scenarios are hidden unless you ask for them."
                : "A scenario is where the packages, costs and adoption live. Fork the baseline to get one of your own."
            }
            action={
              !filtered && detail.abilities.canCreateScenario ? (
                <Button size="sm" onClick={() => setCreating(true)}>
                  New scenario
                </Button>
              ) : undefined
            }
          />
        )}

        {compareIds.length > 0 && (
          <div
            role="status"
            className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line bg-chip-bg px-3 py-2"
          >
            <p className="text-xs text-ink">
              <span className="font-semibold">
                {compareIds.length} of {MAX_COMPARE_SCENARIOS} picked for comparison.
              </span>{" "}
              {compareIds.length === 1
                ? "Tick one more — two is the useful minimum."
                : "Open them side by side, with a chart and a table of every input that differs."}
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={compareIds.length < 2}
                onClick={() => void navigate({ to: "/compare", search: compareSearch(compareIds) })}
              >
                Compare {compareIds.length}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setCompareIds([])}>
                Clear
              </Button>
            </div>
          </div>
        )}

        {rows.length > 0 && (
          <ScenarioTable
            scenarios={rows}
            productSlug={detail.slug}
            currencyCode={detail.currencyCode}
            busyId={busyId}
            selectedIds={compareIds}
            canSelectMore={compareIds.length < MAX_COMPARE_SCENARIOS}
            onToggleSelect={(scenario) => toggleCompare(scenario.id)}
            onDuplicate={(scenario) => duplicate.mutate({ id: scenario.id })}
            onShare={(scenario) => setSharing(scenario)}
            onArchive={(scenario) => {
              if (scenario.archivedAt) archiveScenario.mutate({ id: scenario.id, restore: true });
              else setConfirmArchiveScenario(scenario);
            }}
            onSetBaseline={(scenario) =>
              patchScenario.mutate({ id: scenario.id, input: { isBaseline: true } })
            }
          />
        )}

        {(duplicate.isError || archiveScenario.isError || patchScenario.isError) && (
          <ErrorNotice
            error={duplicate.error ?? archiveScenario.error ?? patchScenario.error}
            title="That action failed"
          />
        )}
      </Panel>

      <ProductMembers product={detail} />

      <Modal open={editing} title="Edit product" onClose={() => setEditing(false)}>
        <EditProductForm product={detail} onDone={() => setEditing(false)} />
      </Modal>

      <NewScenarioDialog
        product={detail}
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(scenario) => {
          setCreating(false);
          void navigate({
            to: "/products/$slug/scenarios/$scenarioId",
            params: { slug: detail.slug, scenarioId: scenario.id },
          });
        }}
      />

      <ImportScenarioDialog
        product={detail}
        open={importing}
        onClose={() => setImporting(false)}
        onImported={(scenario) => {
          setImporting(false);
          void navigate({
            to: "/products/$slug/scenarios/$scenarioId",
            params: { slug: detail.slug, scenarioId: scenario.id },
          });
        }}
      />

      <ShareDialog scenario={sharing} open={sharing !== null} onClose={() => setSharing(null)} />

      <ConfirmDialog
        open={confirmArchiveProduct}
        title="Archive this product?"
        body="Its scenarios become read-only and it drops to the bottom of the product list. Nothing is deleted, and you can restore it from this page."
        confirmLabel="Archive product"
        tone="danger"
        busy={archiveProduct.isPending}
        error={archiveProduct.isError ? describeError(archiveProduct.error) : null}
        onCancel={() => setConfirmArchiveProduct(false)}
        onConfirm={() =>
          archiveProduct.mutate(false, { onSuccess: () => setConfirmArchiveProduct(false) })
        }
      />

      <ConfirmDialog
        open={confirmArchiveScenario !== null}
        title="Archive this scenario?"
        body={
          <>
            “{confirmArchiveScenario?.name}” is hidden from the list and can no longer be edited.
            Nothing is deleted — tick “include archived” to find it and restore it.
          </>
        }
        confirmLabel="Archive scenario"
        tone="danger"
        busy={archiveScenario.isPending}
        error={archiveScenario.isError ? describeError(archiveScenario.error) : null}
        onCancel={() => setConfirmArchiveScenario(null)}
        onConfirm={() => {
          if (!confirmArchiveScenario) return;
          archiveScenario.mutate(
            { id: confirmArchiveScenario.id },
            { onSuccess: () => setConfirmArchiveScenario(null) },
          );
        }}
      />
    </div>
  );
}

/** Split out so the form remounts — and so resets its state — with every open. */
function EditProductForm({ product, onDone }: { product: ProductDetail; onDone: () => void }) {
  const update = useUpdateProduct(product.id);

  return (
    <ProductForm
      initial={{
        name: product.name,
        description: product.description ?? "",
        currencyCode: product.currencyCode,
        defaultHorizonYears: product.defaultHorizonYears,
        defaultCommitmentYears: product.defaultCommitmentYears,
        status: product.status,
      }}
      currencyLocked={product.currencyLocked}
      submitLabel="Save changes"
      busy={update.isPending}
      error={update.error}
      onCancel={onDone}
      onSubmit={(input) => update.mutate(input, { onSuccess: onDone })}
    />
  );
}
